import { Box } from '@deckee/deck-hand';
import React, { FC, useCallback, useMemo } from 'react';
import { Customized } from 'recharts';
import { useTheme } from 'styled-components';
import {
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
} from '../charts/chartDefaults';
import { CHART_LAYOUT } from '../../../../shared/charts/chart-layout';
import {
  formatHourShort,
  nearestPointIndex,
  simpleDayNightBands,
} from '../../../../shared/charts/chart-utils';
import { RechartsScrubbableChart } from '../../../../shared/charts/recharts';
import type { WeatherRiskGraphPoint } from '../../helpers/risk/riskLevels';
import {
  buildRiskBarSegments,
  getForecastDayChartDomain,
  getRiskLevelAtIndex,
  getRiskLevelColor,
  RISK_BAR_HEIGHT,
  RISK_CHART_PLOT_HEIGHT,
  riskGraphPointsToScrubSeries,
  riskScrubPointsToTimeSeries,
} from './scrubGraph';

export interface RiskScrubGraphProps {
  riskPoints: WeatherRiskGraphPoint[];
  selectedDate: string;
  timeZone: string;
  selectedTime?: Date | null;
  defaultSelectedTime?: Date;
  onSelectedTimeChange?: (time: Date, index: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

const RiskScrubGraph: FC<RiskScrubGraphProps> = ({
  riskPoints,
  selectedDate,
  timeZone,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();

  const domain = useMemo(
    () => getForecastDayChartDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const scrubPoints = useMemo(
    () => riskGraphPointsToScrubSeries(riskPoints, timeZone),
    [riskPoints, timeZone],
  );

  const chartData = useMemo(
    () => riskScrubPointsToTimeSeries(scrubPoints),
    [scrubPoints],
  );

  const segments = useMemo(
    () => buildRiskBarSegments(scrubPoints, domain),
    [domain, scrubPoints],
  );

  const backgroundBands = useMemo(
    () => simpleDayNightBands(domain.start, domain.end, { timeZone }),
    [domain, timeZone],
  );

  const formatXTick = useMemo(
    () => (tick: Date) => formatHourShort(tick, timeZone),
    [timeZone],
  );

  const renderRiskBars = useCallback(
    (layerProps: {
      xAxisMap?: Record<string, { scale: (value: number) => number }>;
    }) => {
      const xAxisKey =
        layerProps.xAxisMap && Object.keys(layerProps.xAxisMap)[0];
      const xAxis = xAxisKey ? layerProps.xAxisMap?.[xAxisKey] : undefined;
      if (!xAxis) return null;

      const plotTop = CHART_LAYOUT.SCRUBBER_LABEL_HEIGHT;
      const barTop = plotTop + (RISK_CHART_PLOT_HEIGHT - RISK_BAR_HEIGHT) / 2;

      return (
        <g>
          {segments.map((segment) => {
            const x = Math.floor(xAxis.scale(segment.startTime.getTime()));
            const xEnd = Math.ceil(xAxis.scale(segment.endTime.getTime()));
            const width = Math.max(0, xEnd - x);

            return (
              <rect
                key={`${segment.startTime.getTime()}-${segment.riskLevel}`}
                x={x}
                y={barTop}
                width={width}
                height={RISK_BAR_HEIGHT}
                fill={getRiskLevelColor(segment.riskLevel, theme)}
                shapeRendering="crispEdges"
              />
            );
          })}
        </g>
      );
    },
    [segments, theme],
  );

  if (chartData.length === 0) {
    return null;
  }

  const selectedIndex =
    selectedTime != null
      ? nearestPointIndex(chartData, selectedTime)
      : defaultSelectedTime != null
        ? nearestPointIndex(chartData, defaultSelectedTime)
        : 0;

  const scrubberDotColor = getRiskLevelColor(
    getRiskLevelAtIndex(scrubPoints, selectedIndex) ?? 'caution',
    theme,
  );

  return (
    <Box width="100%" data-testid="weather-forecast-risk-scrub-graph">
      <RechartsScrubbableChart
        data={chartData}
        domain={domain}
        timeZone={timeZone}
        plotHeight={RISK_CHART_PLOT_HEIGHT}
        xTickIntervalHours={3}
        hideMidnightTicks
        formatXTick={formatXTick}
        xTickColor={theme.colors.darkergrey}
        backgroundBands={backgroundBands}
        showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
        yDomain={[0, 1]}
        selectedTime={selectedTime}
        defaultSelectedTime={defaultSelectedTime}
        onSelectedTimeChange={onSelectedTimeChange}
        onScrubStart={onScrubStart}
        onScrubEnd={onScrubEnd}
        showScrubberDot
        scrubberDotPosition="plot-center"
        scrubberDotRadius={12}
        scrubberDotColor={scrubberDotColor}
        plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
        chromeColors={WEATHER_CHART_CHROME_COLORS}
      >
        {() => <Customized component={renderRiskBars as unknown as never} />}
      </RechartsScrubbableChart>
    </Box>
  );
};

export default RiskScrubGraph;
